import { SearchIcon } from '@primer/octicons-react';
import InterfaceSearch from '@streamlinehq/streamlinehq/img/streamline-mini-line/interface-essential/search/interface-search.svg';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ContextSelector } from '@upright/ui/common/generic/components/context-selector';
import { Input } from '@upright/ui/common/generic/components/input';
import { useUnifiedShell } from '@upright/ui/views/unified-shell/use-unified-shell';
import { useSelectableHomeOrganizations } from '../use-selectable-home-organizations';
import { useSelectedOrganization } from '../selected-organization-context';
import { Organization } from '@upright/common/iam/organizations/organization-types';
import styles from './organization-selector-sidebar.module.css';

/**
 * The legacy menu's avatar ground: `teal-500` for every organisation, the same as the default
 * trigger's and the same as the corporate selector's. The mark identifies an entity, it does not
 * encode anything about it (`design/40-shell-and-pages.md` "Sidebar"). The unified menu draws no
 * avatar.
 *
 * This used to hash the organisation id across teal, blue-400 and purple, on the stated grounds of
 * mirroring the DMA company selector — which has only ever drawn the first entry of its own palette,
 * so the two tools were being kept consistent with something that was not happening. What the hash
 * did instead was make one organisation a different colour in one customer's rail than in another's,
 * and change colour when an id changed.
 */
const ORG_AVATAR_BG_COLOR = 'var(--color__teal-500)';

function avatarLetterFor(name: string | undefined): string {
  return name ? name.trim().slice(0, 1).toUpperCase() : '?';
}

/**
 * The organisation selector at the top of the investor rail, in both shells: `MainNavigation` mounts
 * it under the dark header, and the unified shell's rail mounts it too (through `MainNavigation`
 * today, through `UnifiedInvestorNavigation` once PLT-6528 lands). That is why the menu is *gated*
 * rather than simply rebuilt.
 *
 * Two menus, picked by `useUnifiedShell` (PLT-6648, Janne's review of the shell on staging):
 *
 * - **unified UI** — the rebuilt menu, matching the design system's dropdown popover. Everything
 *   Janne's screenshot showed is fixed there: a popover that no longer overhangs the content column,
 *   list spacing that reads as a list, the design system's `Input` for the search field, and the
 *   `--focus-ring__*` tokens. Its rows are the design system's menu rows: the name alone, regular
 *   weight, on the dropdown's gray hover and selected grounds.
 * - **everyone else** — the shipped `styled-components` menu, unchanged, down to its
 *   `@primer/octicons-react` magnifier and its `--borderColor-*` Primer variables.
 *
 * Keeping the old one is the point of the branch and not reluctance to delete it. The unified shell
 * is opt-in and early (PLT-6530), so work done for it must not reach an investor who has not been
 * opted in — a visual-only change is still a change to a customer who did not ask for one. The
 * legacy half goes when the legacy investor home does, and this file collapses back to one menu.
 *
 * `useUnifiedShell` is read here rather than passed down from the frame because it is *the* gate the
 * header switch, the frame and the home-view switch all read, and a fourth reader of one hook cannot
 * disagree with them the way a threaded prop could. It never loads by the time this renders: the
 * route tree has already resolved the same gate to choose the frame this rail sits in.
 */
export function OrganizationSelectorSidebar() {
  const { selectedOrganization, setSelectedOrganization } = useSelectedOrganization();
  const { enabled: unifiedShell } = useUnifiedShell();
  const organizations = useSelectableHomeOrganizations() ?? [];

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus the search input when the dropdown opens, reset query when it closes. The ref is the
  // legacy field's only — the unified menu's field is the design system's `Input` and autofocuses
  // itself on mount, so the optional call is a no-op on that branch. Resetting is shared: both
  // menus unmount when they close, so a menu reopened after a click outside must not come back
  // still filtered.
  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  const filteredOrganizations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return organizations;
    return organizations.filter(o => o.name.toLowerCase().includes(q));
  }, [organizations, searchQuery]);

  if (organizations.length <= 1) return null;

  /* An organisation is always selected by the time this renders: the resolver falls back to a
     default whenever the URL names none or names one the viewer cannot reach, and this component
     has already returned above unless there are at least two to choose from. The fallback is for
     the frame before the organisations query resolves. */
  const selectedName = selectedOrganization?.name ?? '';

  const handleSelect = (org: Organization) => {
    setIsOpen(false);
    setSelectedOrganization(org);
    dropdownRef.current?.querySelector('button')?.focus();
  };

  return (
    <div
      className={styles.container}
      ref={dropdownRef}
      onBlur={event => {
        if (unifiedShell && !event.currentTarget.contains(event.relatedTarget)) setIsOpen(false);
      }}
      onKeyDown={event => {
        if (unifiedShell && !isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
          event.preventDefault();
          event.stopPropagation();
          setIsOpen(true);
        }
      }}
    >
      {/*
        The box, the avatar, the name's truncation and the caret are the design system's as of
        PLT-6220 — this rail drew its own until then, and so did the DMA rail and the prototype, from
        one spec. It is shared by both branches below: the trigger was already on spec in Janne's
        screenshot, and only the menu under it was not.

        `menu` given, so the box renders as a trigger. This rail returns `null` above at one
        organisation or none, so whenever it renders at all it opens a menu — there is no static
        variant of the investor selector.
      */}
      <ContextSelector
        name={selectedName}
        variant={unifiedShell ? 'text' : 'default'}
        menu={{
          isOpen,
          onToggle: () => {
            setIsOpen(open => !open);
          },
        }}
      />

      {isOpen && unifiedShell && (
        <div
          className={styles.menu}
          ref={menuRef}
          onKeyDown={event => {
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              setIsOpen(false);
              dropdownRef.current?.querySelector('button')?.focus();
              return;
            }
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
            event.preventDefault();
            event.stopPropagation();
            const rows = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []);
            if (rows.length === 0) return;
            const index = rows.findIndex(row => row === document.activeElement);
            const next =
              index < 0
                ? event.key === 'ArrowDown'
                  ? 0
                  : rows.length - 1
                : (index + (event.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length;
            rows[next].focus();
          }}
        >
          {/*
            No group label above the field and no monogram on the rows. The menu drops out of the
            selector that already names the organisation, and it holds one group, so a label would
            name the only thing in it; the field's own placeholder says what it searches. The trigger
            is the text variant (design/40-shell-and-pages.md "Sidebar": the unified selector omits
            the monogram), and a teal tile on every row would put the positive-impact hue on seven
            things that encode nothing (design/10-foundations.md "Every colour has one job").
          */}
          {/*
            The design system's field, so the focus ring is the platform one on a full-height box.
            The magnifier is Streamline, the sanctioned family, imported from the package rather
            than from `@upright/ui/icons` because the curated set has no plain magnifier in it —
            only "search dollar" and "zoom document". The shell's own search field does the same
            (`views/unified-shell/`).
          */}
          <Input
            label="Search organizations"
            isLabelVisuallyHidden
            placeholder="Search organizations"
            startAdornment={InterfaceSearch}
            value={searchQuery}
            onChange={setSearchQuery}
            autoFocus
          />
          <div className={styles.list}>
            {filteredOrganizations.map(org => {
              const isSelected = org.id === selectedOrganization?.id;
              return (
                <button
                  key={org.id}
                  type="button"
                  className={styles.row}
                  data-selected={isSelected || undefined}
                  aria-pressed={isSelected}
                  onMouseDown={event => {
                    // Safari does not focus buttons on click. Keep focus in the menu until
                    // onClick selects the row, so the container's blur handler cannot unmount it.
                    event.preventDefault();
                  }}
                  onClick={() => {
                    handleSelect(org);
                  }}
                >
                  <span className={styles.name} title={org.name}>
                    {org.name}
                  </span>
                </button>
              );
            })}
            {filteredOrganizations.length === 0 && <div className={styles.empty}>No matching organizations</div>}
          </div>
        </div>
      )}

      {isOpen && !unifiedShell && (
        <DropdownMenu>
          <MenuHeader>ORGANIZATION</MenuHeader>
          <SearchField>
            <SearchIconWrap aria-hidden>
              <SearchIcon size={14} />
            </SearchIconWrap>
            <SearchInput
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
              }}
              placeholder="Search organizations..."
              aria-label="Search organizations"
            />
          </SearchField>
          <MenuDivider />
          <OrgList>
            {filteredOrganizations.map(org => {
              const isSelected = org.id === selectedOrganization?.id;
              return (
                <OrgRow
                  key={org.id}
                  type="button"
                  $isSelected={isSelected}
                  onClick={() => {
                    handleSelect(org);
                  }}
                >
                  <Avatar $bgColor={ORG_AVATAR_BG_COLOR} aria-hidden>
                    {avatarLetterFor(org.name)}
                  </Avatar>
                  <OrgNameInMenu title={org.name}>{org.name}</OrgNameInMenu>
                </OrgRow>
              );
            })}
            {filteredOrganizations.length === 0 && <NoResults>No matching organizations</NoResults>}
          </OrgList>
        </DropdownMenu>
      )}
    </div>
  );
}

/*
 * Everything below is the shipped menu, for users who are not on the unified UI, kept exactly as it
 * was — including the parts the rebuilt menu above exists to fix. Do not tidy it: its whole job is
 * to render byte-for-byte what a legacy investor rail rendered before PLT-6648, so that a change
 * made for the unified shell cannot reach a customer who has not been opted into it. It goes when
 * the legacy investor home does.
 *
 * `DropdownContainer` is the one piece that did not survive: it was `position: relative; width:
 * 100%`, which is `.container` in the CSS module, so both branches now share that one wrapper.
 */

/**
 * The menu rows' avatar. The trigger's is `ContextSelector`'s own, so this one is no longer shared
 * with it and is free to differ where the menu needs it to — the gray "None" square is the case.
 */
const Avatar = styled.div<{ $bgColor: string }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.$bgColor};
  color: var(--fgColor-onEmphasis);
  font-size: 18px;
  line-height: 18px;
  font-weight: 600;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 292px;
  width: max-content;
  max-width: 500px;
  background-color: var(--bgColor-default);
  border: 1px solid var(--borderColor-default);
  border-radius: var(--border-radius__md);
  box-shadow: 0 2px 16px 0 hsla(212, 9%, 38%, 0.25);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  padding: var(--space__md) var(--space__sm);
  gap: var(--space__md);
`;

const MenuHeader = styled.div`
  font-size: 12px;
  font-weight: var(--font-weight__bold);
  color: var(--fgColor-muted);
  letter-spacing: 0.5px;
  padding: 0 var(--space__xs);
`;

const MenuDivider = styled.hr`
  border: none;
  border-top: 1px solid var(--borderColor-default);
  margin: 0;
`;

const OrgList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__md);
  max-height: calc(5 * 50px);
  overflow-y: auto;
`;

const OrgRow = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  text-align: left;
  padding: 6px;
  border: none;
  background: ${props => (props.$isSelected ? 'var(--color__blue-50)' : 'transparent')};
  border-radius: var(--border-radius__md);
  cursor: pointer;

  &:hover {
    background: ${props => (props.$isSelected ? 'var(--color__blue-50)' : 'var(--bgColor-muted)')};
  }

  &:focus-visible {
    outline: 2px solid var(--focus-outlineColor);
    outline-offset: 2px;
  }
`;

const OrgNameInMenu = styled.span`
  flex: 1;
  min-width: 0;
  font-size: var(--font-size__sm);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SearchField = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space__xs);
  padding-inline: var(--space__sm);
  padding-block: var(--space__xs);
  border: 1px solid var(--color__gray-300);
  border-radius: var(--border-radius__md);
  background-color: var(--color__white);

  &:focus-within {
    border-color: var(--color__gray-400);
    outline: var(--focus-ring__width) solid var(--focus-ring__color);
    outline-offset: var(--focus-ring__offset);
  }
`;

const SearchIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--color__gray-500);
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;

  /* The whole field is the focus target — the ring is on SearchField's
     :focus-within above. Suppressing it here has to be :focus-visible-scoped to
     out-specify the app-wide input ring baseline in reset.css. */
  &:focus-visible {
    outline: none;
  }

  font-size: var(--font-size__md);
  color: var(--color__gray-800);

  &::placeholder {
    color: var(--color__gray-500);
  }
`;

const NoResults = styled.div`
  padding: var(--space__sm);
  font-size: var(--font-size__sm);
  color: var(--color__gray-500);
  text-align: center;
`;
